/**
 * MobileHeader — compact sticky header for mobile participant view.
 * Shows: emblem + name (truncated) + round + phase + timer + text-size + menu
 */

import { useState, useEffect } from 'react'
import { CountryEmblem } from '@/components/CountryEmblem'
import { supabase } from '@/lib/supabase'

const FONT_SCALES = [1, 1.2, 1.4] as const
const FONT_LABELS = ['A', 'A+', 'A++'] as const
const STORAGE_KEY = 'ttt_mobile_font_scale'

interface Props {
  characterName: string
  countryCode: string
  color: string
  round: number
  phase: string
  remaining: number | null
  status: string
  hasRole: boolean
  displayName?: string
  onNameTap?: () => void
}

function fmtTimer(s: number): string {
  const rounded = Math.floor(s)
  const neg = rounded < 0
  const abs = Math.abs(rounded)
  const m = Math.floor(abs / 60)
  const sec = abs % 60
  return `${neg ? '-' : ''}${m}:${sec.toString().padStart(2, '0')}`
}

export function MobileHeader({ characterName, countryCode, color, round, phase, remaining, status, hasRole, displayName, onNameTap }: Props) {
  const [scaleIndex, setScaleIndex] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? Math.min(Number(stored), 2) : 0
  })
  const [showMenu, setShowMenu] = useState(false)

  useEffect(() => {
    document.documentElement.style.fontSize = `${FONT_SCALES[scaleIndex] * 100}%`
    localStorage.setItem(STORAGE_KEY, String(scaleIndex))
    return () => { document.documentElement.style.fontSize = '' }
  }, [scaleIndex])

  const isOvertime = remaining !== null && remaining < 0
  const isPaused = status === 'paused'
  const isProcessing = status === 'processing'

  return (
    <header className="sticky top-0 z-40 bg-card border-b-2 px-3 py-2" style={{ borderBottomColor: color }}>
      <div className="flex items-center justify-between gap-2">
        {/* Left: identity */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {hasRole ? (
            <button onClick={onNameTap} className="flex items-center gap-2 min-w-0">
              <CountryEmblem countryId={countryCode} color={color} size={24} />
              <span className="font-heading text-body-sm font-semibold text-text-primary truncate max-w-[120px]">
                {characterName}
              </span>
            </button>
          ) : (
            <span className="font-body text-body-sm text-text-secondary truncate">
              {displayName || 'Awaiting role...'}
            </span>
          )}
        </div>

        {/* Center: status badges */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {isPaused && <span className="font-data text-[9px] font-bold text-warning bg-warning/10 px-1.5 py-0.5 rounded">PAUSED</span>}
          {isProcessing && <span className="font-data text-[9px] font-bold text-warning bg-warning/10 px-1.5 py-0.5 rounded animate-pulse">PROC</span>}
          {phase === 'B' && !isProcessing && <span className="font-data text-[9px] font-bold text-action bg-action/10 px-1.5 py-0.5 rounded">BREAK</span>}
        </div>

        {/* Right: round + timer + controls */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="font-data text-body-sm font-bold text-text-primary">R{round}</span>
          {remaining !== null && (
            <span className={`font-data text-body-sm font-bold tabular-nums ${isOvertime ? 'text-danger animate-pulse' : 'text-text-primary'}`}>
              {isOvertime ? 'OT' : fmtTimer(remaining)}
            </span>
          )}
          <button
            onClick={() => setScaleIndex(i => (i + 1) % 3)}
            className="font-data text-[10px] font-bold text-text-secondary bg-base px-1.5 py-1 rounded min-w-[28px] text-center"
            title="Text size"
          >
            {FONT_LABELS[scaleIndex]}
          </button>
          <button
            onClick={() => setShowMenu(m => !m)}
            className="text-text-secondary hover:text-text-primary px-1 py-1 text-body-sm"
          >
            &#x22EE;
          </button>
        </div>
      </div>

      {/* Dropdown menu */}
      {showMenu && (
        <div className="absolute right-2 top-full mt-1 bg-card border border-border rounded-lg shadow-xl z-50 py-1 min-w-[140px]">
          <button
            onClick={async () => { await supabase.auth.signOut(); window.location.href = '/login' }}
            className="w-full text-left px-4 py-2.5 font-body text-body-sm text-danger hover:bg-base transition-colors"
          >
            Sign Out
          </button>
        </div>
      )}
    </header>
  )
}
